// alpha-stream.ts — fetch-based SSE subscriber for the alpha session feed.
//
// EventSource can't carry an Authorization header, but the JWT lives in
// localStorage (not a cookie), so we hand-roll SSE on top of fetch +
// ReadableStream — same pattern used by api/chat.ts. The backend stream
// endpoint pushes one JSON-encoded event per `data:` line, separated by
// blank lines, with optional `: keep-alive` comments every 15s.

import type { AlphaCard, AnalystView, DebateRound } from './alpha';

export type AlphaStreamEvent =
  | { type: 'view'; sessionId: number; payload: AnalystView }
  | { type: 'round'; sessionId: number; payload: DebateRound }
  | { type: 'card'; sessionId: number; payload: AlphaCard }
  | { type: 'stage'; sessionId: number; stage: string }
  | { type: 'context'; sessionId: number; payload: {
      indices?: string[]; quotes?: string[]; news?: string[]; holdingCodes?: string[];
    } }
  | { type: 'bull_bear'; sessionId: number; payload: { bull?: string; bear?: string } }
  | { type: 'risk_stance'; sessionId: number; payload: { stance?: string; content?: string } }
  | { type: 'risk_summary'; sessionId: number; payload: { adjustedConfidence?: number; summary?: string } }
  // New: emitted by service.recordLLMTrace as soon as a chatOnce call lands
  // a row in alpha_llm_trace. Carries metadata only — full prompt + CoT
  // body are fetched via getLLMTrace(id) when the user expands the row.
  | { type: 'trace'; sessionId: number; payload: {
      id: number; stage: string; model: string;
      durationMs: number;
      promptTokens?: number; completionTokens?: number;
      reasoningTokens?: number; totalTokens?: number;
      hasCot?: boolean; success: boolean; errorMsg?: string;
      createdAt: number;
    } }
  // Token-delta chunk from a streaming LLM call. Today only emitted by
  // RunDebateStream (research manager stage). `delta` is the raw next
  // fragment — NOT cumulative; the frontend accumulates per (sessionId, stage).
  | { type: 'token'; sessionId: number; stage: string; payload: { delta: string } }
  // Live "AI 正在做什么" status — emitted right before each LLM call and
  // by a 5s heartbeat ticker during the call itself. agentType is the
  // logical producer ("fundamental"|"technical"|"sentiment"|"news"|
  // "bull"|"bear"|"research_manager"|"risk_aggressive"|...).
  // kind distinguishes:
  //   - "step":      a discrete milestone (load data / call LLM / parse).
  //                  Appended to the per-agent timeline.
  //   - "heartbeat": 5s in-place tick ("思考中 Ns"). Refreshes the current
  //                  status only; does NOT append to the timeline.
  // (Heartbeat events may omit detail or repeat the agent name — frontend
  //  must preserve the prior step's detail to avoid name duplication.)
  | { type: 'activity'; sessionId: number; payload: {
      agentType: string; kind?: 'step' | 'heartbeat'; label: string; detail: string;
    } }
  // Zero-cost "盯盘中" beacon fired by the worker every ~30s BETWEEN
  // multi-agent rounds (no LLM, not persisted). nextRoundInSec is the
  // countdown to the next full analysis round. The frontend shows it
  // only while the live "AI 正在工作" panel is otherwise empty, so the
  // UI never looks idle during the inter-round gap.
  | { type: 'market_watch'; sessionId: number; payload: { nextRoundInSec: number; steps?: string[] } };

function getToken(): string {
  return localStorage.getItem('alpha_token') || localStorage.getItem('token') || '';
}

interface Options {
  onEvent: (e: AlphaStreamEvent) => void;
  onConnected?: () => void;
  onError?: (msg: string) => void;
}

// subscribeAlphaStream opens a long-lived SSE connection for the given
// session. Returns a cancel function. The subscriber reconnects with a
// 2s backoff on transient failures (network blip, server restart) and
// gives up only when the consumer calls cancel.
export function subscribeAlphaStream(sessionId: number, opts: Options): () => void {
  const ctrl = new AbortController();

  (async () => {
    while (!ctrl.signal.aborted) {
      try {
        const token = getToken();
        const res = await fetch(`/api/alpha-lab/sessions/${sessionId}/stream`, {
          method: 'GET',
          headers: {
            Accept: 'text/event-stream',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          signal: ctrl.signal,
        });
        if (!res.ok || !res.body) {
          opts.onError?.(`stream HTTP ${res.status}`);
          // 4xx is permanent — bail. 5xx falls through to retry.
          if (res.status >= 400 && res.status < 500) return;
          await delay(2000);
          continue;
        }

        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buf = '';
        let announcedConnect = false;
        try {
          while (!ctrl.signal.aborted) {
            const { done, value } = await reader.read();
            if (done) break;
            if (!announcedConnect) {
              announcedConnect = true;
              opts.onConnected?.();
            }
            buf += decoder.decode(value, { stream: true });
            const lines = buf.split('\n');
            buf = lines.pop() ?? '';
            for (const raw of lines) {
              const line = raw.replace(/\r$/, '');
              if (!line.startsWith('data:')) continue; // ignore comments / event: lines
              const data = line.slice(5).trim();
              if (!data) continue;
              try {
                const parsed = JSON.parse(data) as AlphaStreamEvent;
                opts.onEvent(parsed);
              } catch {
                // Malformed frame — skip silently.
              }
            }
          }
        } finally {
          reader.cancel().catch(() => {});
        }
      } catch (err) {
        if (ctrl.signal.aborted) return;
        opts.onError?.(String(err));
      }
      // Reconnect after a short delay.
      await delay(2000);
    }
  })();

  return () => ctrl.abort();
}

function delay(ms: number): Promise<void> {
  return new Promise(r => setTimeout(r, ms));
}
